import { Flex, Text } from '@byecode/ui'
import type { ActorEvent, RecordLikeProtocol, SizeConfigure, VariableADTvalue, ViewStyles } from '@lighthouse/core'
import type { FlowLayoutNode } from '@lighthouse/shared'
import {
    fieldConvertValue,
    getBackgroundStyle,
    getRecordPoolKey,
    getTextColorStyle,
    useAppThemeContext,
    useAtomData,
    useFlowLayoutContext
} from '@lighthouse/shared'
import { addDays, eachDayOfInterval, format, getHours, getMinutes, isSameDay, isWithinInterval, lightFormat } from 'date-fns'
import React, { memo, useCallback, useDeferredValue, useMemo } from 'react'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context/ViewBlockContext'
import { Item } from '../CustomViewBlock/Item'
import { setDeadCopyNode } from '../CustomViewBlock/utils'

const Root = styled.div<{ showBorder?: boolean; borderColor?: string }>`
    ${({ showBorder, borderColor }) => `
        --border-size: ${showBorder ? 1 : 0}px;
        --border-color: ${borderColor || ''};
    `};

    position: relative;
    overflow: auto hidden;
    border-top: var(--border-size) solid var(--border-color);
    border-left: var(--border-size) solid var(--border-color);
`

const Table = styled.table`
    min-width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    border-spacing: 0;
`
const Thead = styled.thead`
    & > tr > th {
        padding: 8px;
        font-size: 14px;
        font-weight: 400;
        border-right: var(--border-size) solid var(--border-color);
        border-bottom: var(--border-size) solid var(--border-color);
    }
`
const Tbody = styled.tbody`
    & > tr > td {
        padding: 8px;
        border-right: var(--border-size) solid var(--border-color);
        border-bottom: var(--border-size) solid var(--border-color);
    }
`

const FIRST_CELL_WIDTH = 80

interface Data {
    time: string
    records: RecordLikeProtocol[]
    timestamp: number
}

interface CalendarViewProps {
    id?: string
    firstDayOfWeek: Date
    node: FlowLayoutNode
    title: string
    gap: number
    readonly?: boolean
    itemWidth?: number
    calendarStartDateField?: string
    viewStyles?: ViewStyles
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
}

export const CalendarView = memo((props: CalendarViewProps) => {
    const { id, firstDayOfWeek, node, title, gap, readonly, itemWidth, calendarStartDateField, viewStyles, onRecordClick } = props
    const hideTimeSplit = viewStyles?.hideTimeSplit ?? false
    const uniformRowHeight = viewStyles?.uniformRowHeight ?? false

    const { blockData, recordPoints, recordPoolAtom, dataSource } = useViewBlockContext()
    const blockId = blockData.id

    const weeks = useMemo(() => {
        return eachDayOfInterval({ start: firstDayOfWeek, end: addDays(firstDayOfWeek, 6) })
    }, [firstDayOfWeek])

    const records = useAtomData(
        recordPoolAtom,
        useCallback(
            pool => {
                if (!calendarStartDateField) {
                    return []
                }

                const field = dataSource.schema[calendarStartDateField]
                if (!field || field.innerType !== 'DATE') {
                    return []
                }

                return recordPoints
                    .map(p => pool.get(getRecordPoolKey(dataSource.id, p.id)))
                    .filter((item): item is RecordLikeProtocol => {
                        if (!item) {
                            return false
                        }

                        const fieldValue = item.content[calendarStartDateField]?.value
                        if (typeof fieldValue !== 'number') {
                            return false
                        }
                        // 不在该周区间的记录过滤掉
                        return isWithinInterval(fieldValue, { start: weeks[0], end: addDays(weeks[weeks.length - 1], 1) })
                    })
            },
            [calendarStartDateField, dataSource.id, dataSource.schema, recordPoints, weeks]
        )
    )

    const generateTimes = useCallback(() => {
        if (!calendarStartDateField) {
            return
        }

        const field = dataSource.schema[calendarStartDateField]
        if (!field || field.innerType !== 'DATE') {
            return
        }

        if (hideTimeSplit) {
            return [{ time: '', records, timestamp: 0 }]
        }

        if (records.length === 0) {
            return [{ time: '', records: [], timestamp: 0 }]
        }

        return records
            .reduce<Data[]>((prev, next) => {
                const fieldValue = next.content[calendarStartDateField]?.value
                if (typeof fieldValue !== 'number') {
                    return prev
                }

                const time = lightFormat(fieldValue, 'HH:mm')
                const index = prev.findIndex(item => time === item.time)
                if (index === -1) {
                    prev.push({ time, records: [next], timestamp: getHours(fieldValue) * 60 + getMinutes(fieldValue) })
                } else {
                    prev[index].records.push(next)
                }

                return prev
            }, [])
            .sort((a, b) => a.timestamp - b.timestamp)
    }, [calendarStartDateField, dataSource.schema, hideTimeSplit, records])

    const timeList = useMemo(() => generateTimes(), [generateTimes])
    const squareCells = uniformRowHeight && records.length === 0

    const editableRecordId = timeList?.[0]?.records[0]?.id

    const {
        theme: { palettes }
    } = useAppThemeContext()

    const { useParseVariableWithSubscription } = useFlowLayoutContext()
    const parseTrBackgroundColorVariable = useParseVariableWithSubscription(viewStyles?.calendarDateRow?.background?.variable)
    const parseTdBackgroundColorVariable = useParseVariableWithSubscription(viewStyles?.calendarTimeCol?.background?.variable)

    if (!calendarStartDateField) {
        return null
    }

    return (
        <Root
            id={id}
            showBorder={viewStyles?.showGrid ?? true}
            borderColor={getBackgroundStyle({ value: viewStyles?.gridColor, palettes }).backgroundColor}
        >
            <Table>
                <colgroup>
                    {!hideTimeSplit && <col style={{ width: FIRST_CELL_WIDTH }} />}
                    {weeks.map(item => (
                        <col key={item.getTime()} style={itemWidth ? { minWidth: itemWidth } : undefined} />
                    ))}
                </colgroup>
                <Thead>
                    <tr
                        style={getBackgroundStyle({
                            value: viewStyles?.calendarDateRow?.background,
                            palettes,
                            parseVariableColor: (v: VariableADTvalue) => {
                                const field = parseTrBackgroundColorVariable(v)
                                return field ? fieldConvertValue(field) : ''
                            }
                        })}
                    >
                        {!hideTimeSplit && (
                            <th style={{ width: FIRST_CELL_WIDTH, position: 'sticky', left: 0, zIndex: 3, background: 'inherit' }}>时间</th>
                        )}
                        {weeks.map(item => (
                            <th key={item.getTime()} style={itemWidth ? { minWidth: itemWidth } : undefined}>
                                <Text size={12} style={getTextColorStyle(viewStyles?.calendarDateRow?.dateColor, palettes)}>
                                    {lightFormat(item, 'MM/dd')}
                                </Text>
                                <Text style={getTextColorStyle(viewStyles?.calendarDateRow?.weekColor, palettes)}>
                                    {format(item, 'EEE')}
                                </Text>
                            </th>
                        ))}
                    </tr>
                </Thead>
                <Tbody>
                    {timeList?.map((time, timeIndex) => (
                        <tr key={time.time}>
                            {!hideTimeSplit && (
                                <td
                                    style={{
                                        width: FIRST_CELL_WIDTH,
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 3,
                                        fontSize: 14,
                                        textAlign: 'center',
                                        ...getBackgroundStyle({
                                            value: viewStyles?.calendarTimeCol?.background,
                                            palettes,
                                            parseVariableColor: (v: VariableADTvalue) => {
                                                const field = parseTdBackgroundColorVariable(v)
                                                return field ? fieldConvertValue(field) : ''
                                            }
                                        }),
                                        ...getTextColorStyle(viewStyles?.calendarTimeCol?.timeColor, palettes)
                                    }}
                                >
                                    {time.time}
                                </td>
                            )}

                            {weeks.map((week, weekIndex) => {
                                const inRangeRecords = time.records.filter(r => {
                                    const value = r.content[calendarStartDateField]?.value
                                    if (!value || typeof value !== 'number') {
                                        return false
                                    }

                                    return isSameDay(value, week)
                                })

                                const minHeightConfigure =
                                    node.children && node.children.length > 0
                                        ? {}
                                        : {
                                              minHeight: {
                                                  size: 80,
                                                  unit: 'px'
                                              } as SizeConfigure
                                          }

                                const showEmptyCard = uniformRowHeight && inRangeRecords.length === 0
                                const fillRow = showEmptyCard && !squareCells

                                return (
                                    <td
                                        key={`${time.time}-${week.valueOf()}`}
                                        style={{
                                            minWidth: itemWidth,
                                            ...(fillRow ? { position: 'relative' } : undefined)
                                        }}
                                    >
                                        <Flex
                                            direction="column"
                                            gap={gap}
                                            style={fillRow ? { position: 'absolute', inset: 8 } : undefined}
                                        >
                                            {showEmptyCard ? (
                                                <Item
                                                    style={squareCells ? { aspectRatio: '1 / 1', width: '100%' } : { flex: 1, width: '100%' }}
                                                    title={title}
                                                    recordId=""
                                                    recordIndexPath={`${timeIndex}_${weekIndex}_empty`}
                                                    readonly
                                                    data={{
                                                        id: `${blockId}/empty_slot/${timeIndex}_${weekIndex}`,
                                                        scope: node.scope,
                                                        type: 'container',
                                                        virtual: true,
                                                        disabled: true,
                                                        data: node.data && {
                                                            design: node.data.design,
                                                            size: {
                                                                width: { size: 'auto' },
                                                                height: { size: 'auto' },
                                                                ...minHeightConfigure
                                                            },
                                                            layout: node.data.layout,
                                                            position: node.data.position
                                                        },
                                                        children: []
                                                    }}
                                                />
                                            ) : (
                                                inRangeRecords.map((record, index) => (
                                                    <Item
                                                        key={record.id}
                                                        data-record-id={record.id}
                                                        recordId={record.id}
                                                        recordIndexPath={`${timeIndex}_${weekIndex}_${index}`}
                                                        readonly={readonly}
                                                        data={{
                                                            id: `${blockId}/${record.id}`,
                                                            scope: node.scope,
                                                            type: 'container',
                                                            disabled: readonly || record.id !== editableRecordId,
                                                            virtual: true,
                                                            data: node.data && {
                                                                design: node.data.design,
                                                                size: {
                                                                    width: {
                                                                        size: 'auto'
                                                                    },
                                                                    height: {
                                                                        size: 'auto'
                                                                    },
                                                                    ...minHeightConfigure
                                                                },
                                                                layout: node.data.layout,
                                                                position: node.data.position
                                                            },
                                                            children: readonly
                                                                ? node.children
                                                                : setDeadCopyNode(
                                                                      node.children || [],
                                                                      record.id === editableRecordId,
                                                                      blockId
                                                                  )
                                                        }}
                                                        title={title}
                                                        onRecordClick={onRecordClick}
                                                    />
                                                ))
                                            )}
                                        </Flex>
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </Tbody>
            </Table>
        </Root>
    )
})
